"use client"

import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, tableCellClasses, TableContainer } from "@mui/material"
import { type FC, useEffect, useState } from "react"
import Box from "@mui/material/Box"
import { useAppDispatch, useAppSelector } from "../../store/hooks"

import styled_ from "@emotion/styled"
import moment from "moment"
import { Utils, VocuherCategory, ReportTypes } from "../../utils/common-utils"
import { ApiStatus } from "../../utils/api.types"
import Filters from "../Filters/Filters"
import { toast } from "react-toastify"

import {
  fetchTdsPayableReport,
  fetchTdsReceivableReport,
  fetchTcsPayableReport,
  fetchTcsReceivableReport,
  downloadTdsPayableReport,
  downloadTdsReceivableReport,
  downloadTdsPayableExcelReport,
  downloadTdsReceivableExcelReport
} from "../../store/reportStore/reportThunk"
import { clearReportsData } from "../../store/reportStore/reportslice"
import ReusableReportHeader from './ReusableReportHeader'
import { useReportShare } from './useReportShare'

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    // border: '1px solid red',
    left: 0,
    bottom: 0, // <-- KEY
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    // border:'none',
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  // '&:nth-of-type(even)': {
  //   backgroundColor: '#fbfbfb',
  //   // border: '0.5px dashed #9da7b7',
  // },
  // '&:nth-of-type(odd)': {
  //   backgroundColor: '#e9ecf1',
  //   // border: '0.5px dashed #9da7b7',
  // },
  // // hide last border
  // '&:last-child td, &:last-child th': {
  // },
}));

interface TaxReportProps {
  type: string
}

interface ColumnConfig {
  key: string
  label: string
  type: "text" | "number" | "date" | "currency" | "percentage"
  format?: (value: any) => string
}

const TdsTcsTaxReport: FC<TaxReportProps> = (props) => {
  const dispatch = useAppDispatch()
  const { selectedAccount } = useAppSelector((state) => state.auth)
  const { reportsData, status } = useAppSelector(state => state.reports)
  const [filters, setFilters] = useState("")
  const [filterData, setFilterData] = useState<any>()
  const [reportData, setReportData] = useState<any>({ records: [], summary: {} })

  // Dynamic column configurations based on report type
  const getColumnConfig = (): ColumnConfig[] => {
    // Based on the API response structure provided

    // date: _date,
    //     txnId: payment.txnId,
    //     invoiceNumber: invoiceData?.invoiceNumber || '',
    //     partyName: partyData?.legalName || '',
    //     partyPAN: partyData?.taxIdentificationNumber2 || '',
    //     baseAmount: Number(payment.appliedAmountBase || 0).toFixed(2),
    //     taxAmount: Number(payment.appliedAmountTDS || 0).toFixed(2),
    //     totalAmount: Number(payment.appliedAmountTotal || 0).toFixed(2),
    //     invoiceAmount: Number(finalInvoiceAmount || 0).toFixed(2),
    //     tdsAmount: Number(payment.tdsSelected.totalAmount || 0).toFixed(2),
    //     taxName: payment.tdsSelected.tdsSelected.label || '',
    //     taxRate: Number(payment.tdsSelected.tdsSelected.rate || 0),
    //     taxSection: payment.tdsSelected.tdsSelected.taxSection || '',
    //     remarks: payment.remarks || '',
    //     reportType: 'TDS_PAYABLE'

    const commonColumns: ColumnConfig[] = [
      { key: "date", label: "Date", type: "date" },
      { key: "txnId", label: "TRXN ID", type: "text" },
      { key: "invoiceNumber", label: "Invoice Number", type: "text" },
      { key: "partyName", label: "Party Name", type: "text" },
      { key: "partyPAN", label: "Party PAN", type: "text" },
      { key: "baseAmount", label: "Taxable Amount", type: "currency" },
      { key: "totalAmount", label: "Total Amount", type: "currency" },
      { key: "tdsAmount", label: "TDS/TCS Amount", type: "currency" },
      { key: "taxName", label: "Tax Name", type: "text" },
      { key: "taxRate", label: "Tax Rate", type: "text" },
      { key: "taxSection", label: "Tax Section", type: "text" },
      { key: "remarks", label: "Remarks", type: "text" },
    ]

    return commonColumns
  }

  const formatCellValue = (value: any, type: string): string => {
    if (value === null || value === undefined || value === "N/A") {
      return "N/A"
    }

    switch (type) {
      case "date":
        return moment(value).format("DD MMM YYYY")
      case "currency":
        return Utils.formatNumber(Number.parseFloat(value).toFixed(2), false, true)
      case "percentage":
        return typeof value === "number" ? `${value}%` : value
      case "number":
        return Number.parseFloat(value).toFixed(2)
      case "text":
      default:
        // Handle nested objects (like taxDetails)
        if (typeof value === "object" && value !== null) {
          if (value.label) {
            return value.label
          }
          if (value.tdsSelected && value.tdsSelected.label) {
            return value.tdsSelected.label
          }
          return JSON.stringify(value)
        }
        return String(value)
    }
  }

  const filterOnChangeHandler = (data: any) => {
    setFilters(data)
  }

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data)
  }

  const getReportTitle = () => {
    switch (props.type) {
      case ReportTypes.TDS_PAYABLE:
        return "TDS Payable Report"
      case ReportTypes.TDS_RECEIVEABLES:
        return "TDS Receivables Report"
      case ReportTypes.TCS_PAYABLE:
        return "TCS Payable Report"
      case ReportTypes.TCS_RECEIVEABLES:
        return "TCS Receivables Report"
      default:
        return "Tax Report"
    }
  }

  const getVoucherCategory = () => {
    switch (props.type) {
      case ReportTypes.TDS_PAYABLE:
      case ReportTypes.TCS_PAYABLE:
        return VocuherCategory.PURCHASE
      case ReportTypes.TDS_RECEIVEABLES:
      case ReportTypes.TCS_RECEIVEABLES:
        return VocuherCategory.SALE
      default:
        return VocuherCategory.SALE
    }
  }

  const handleDownloadReport = () => {
    const jsonData = JSON.stringify(reportData.records, null, 2)
    const blob = new Blob([jsonData], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${getReportTitle().toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split("T")[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadExcel = async () => {
    const { _id: accountId } = selectedAccount;
    if (!filters) {
      toast.error("Please select date range to download Excel");
      return false;
    }

    // Add isDownloadExcel parameter to filters
    const excelFilters = filters;

    // Determine which thunk to use based on report type
    let downloadThunk;
    let fileName = '';

    if (props.type === ReportTypes.TDS_PAYABLE) {
      downloadThunk = downloadTdsPayableExcelReport;
      fileName = 'tds_payable_report.xlsx';
    } else if (props.type === ReportTypes.TDS_RECEIVEABLES) {
      downloadThunk = downloadTdsReceivableExcelReport;
      fileName = 'tds_receivable_report.xlsx';
    } else {
      toast.error("Excel download not supported for this report type");
      return false;
    }

    return dispatch(downloadThunk({
      accountId: accountId,
      params: excelFilters
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);

        console.log(url);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `${fileName.replace('.xlsx', '')}_${timestamp}.xlsx`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }

  const handleDownloadPDF = async () => {
    const { _id: accountId } = selectedAccount;
    if (!filters) {
      toast.error("Please select date range to download PDF");
      return false;
    }

    // Add isDownloadPdf parameter to filters
    const pdfFilters = filters + '&isDownloadPdf=true';

    // Determine which thunk to use based on report type
    let downloadThunk;
    let fileName = '';

    if (props.type === ReportTypes.TDS_PAYABLE) {
      downloadThunk = downloadTdsPayableReport;
      fileName = 'tds_payable_report.pdf';
    } else if (props.type === ReportTypes.TDS_RECEIVEABLES) {
      downloadThunk = downloadTdsReceivableReport;
      fileName = 'tds_receivable_report.pdf';
    } else {
      toast.error("PDF download not supported for this report type");
      return false;
    }

    return dispatch(downloadThunk({
      accountId: accountId,
      params: pdfFilters
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        console.log(url);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }

  const handlePrint = () => {
    window.print()
  }

  // Share-by-email. Only TDS Payable has a backend share endpoint today; other variants keep no share button.
  const shareEndpoint = props.type === ReportTypes.TDS_PAYABLE ? 'tds-payable' : '';
  const { handleShare: shareHandler, shareTooltip } = useReportShare(shareEndpoint, () => filters);
  const handleShare = shareEndpoint ? shareHandler : undefined;

  useEffect(() => {
    // Clear previous data when component mounts or type changes
    dispatch(clearReportsData());
    
    const { _id: accountId } = selectedAccount
    console.log("props.type === ", props.type);
    const _filters = filters && filters == "" ? undefined : filters
    if (_filters) {
      // Call the appropriate API based on report type
      switch (props.type) {
        case ReportTypes.TDS_PAYABLE:
          dispatch(fetchTdsPayableReport({ accountId, params: _filters }))
          break
        case ReportTypes.TDS_RECEIVEABLES:
          dispatch(fetchTdsReceivableReport({ accountId, params: _filters }))
          break
        case ReportTypes.TCS_PAYABLE:
          dispatch(fetchTcsPayableReport({ accountId, params: _filters }))
          break
        case ReportTypes.TCS_RECEIVEABLES:
          dispatch(fetchTcsReceivableReport({ accountId, params: _filters }))
          break
      }
    }
  }, [dispatch, selectedAccount, filters, props.type])

  // Clear data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearReportsData());
    };
  }, [])

  useEffect(() => {
    // Update report data when API data changes
    if (reportsData && Array.isArray(reportsData)) {
      // Calculate summary from the data
      const summary = {
        totalRecords: reportsData.length,
        totalAmount: reportsData.reduce((sum, record: any) => sum + parseFloat(record.totalAmount || 0), 0),
        totalTaxAmount: reportsData.reduce((sum, record: any) => sum + parseFloat(record.taxAmount || 0), 0),
        totalBaseAmount: reportsData.reduce((sum, record: any) => sum + parseFloat(record.baseAmount || 0), 0),
      }

      setReportData({
        records: reportsData,
        summary: summary
      })
    }
  }, [reportsData])

  const columns = getColumnConfig()

  return (
    <>

      <Grid container spacing={2} >
        <Grid item xs={12}>
          <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2}} className={'c-card2'}>

            <ReusableReportHeader
              reportType={getVoucherCategory()}
              title={getReportTitle()}
              recordCount={reportData.records ? reportData.records.length : 0}
              isLoading={status === ApiStatus.LOADING}
              onDownloadPDF={handleDownloadPDF}
              onDownloadExcel={handleDownloadExcel}
              onDownloadJSON={handleDownloadReport}
              // onPrint={handlePrint}
              onShare={handleShare}
              shareTooltip={shareTooltip}
            />


            <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7' }}>
              <Grid xs={10} >
                <Filters
                  type={getVoucherCategory()}
                  filterHandler={filterOnChangeHandler}
                  selectedFilters={filterdOnChangeHandler}
                  hideFilter={true}
                />
              </Grid>
              <Grid xs={2} >

              </Grid>
            </Grid>
            <div style={{ marginBottom: 12 }}>
              <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <StyledTableCell key={column.key}>{column.label}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(reportsData && reportsData.length > 0) ? reportsData.map((row: any, index) =>
                    <>
                      <StyledTableRow key={index + 1}>
                        <StyledTableCell style={{ width: 80 }}>{row.date}</StyledTableCell>
                        <StyledTableCell style={{ width: 80 }}>{row.txnId}</StyledTableCell>
                        <StyledTableCell style={{ width: 80 }}>{row.invoiceNumber}</StyledTableCell>
                        <StyledTableCell style={{ width: 200 }}>{row.partyName}</StyledTableCell>
                        <StyledTableCell style={{ width: 100 }} >{row.partyPAN}</StyledTableCell>
                        <StyledTableCell>{Utils.formatNumber(row.baseAmount, false, true)}</StyledTableCell>
                        <StyledTableCell>{Utils.formatNumber(row.invoiceAmount, false, true)}</StyledTableCell>
                        <StyledTableCell>{Utils.formatNumber(row.tdsAmount, false, true)}</StyledTableCell>
                        <StyledTableCell>{row.taxName}</StyledTableCell>
                        <StyledTableCell>{row.taxRate} %</StyledTableCell>
                        <StyledTableCell>{row.taxSection}</StyledTableCell>
                        <StyledTableCell>{row.remarks}</StyledTableCell>
                      </StyledTableRow>
                    </>
                  ) :
                    ''
                  }
                </TableBody>
              </Table>
            </div>
          </Box>

        </Grid>
      </Grid>
    </>
  )
}

export default TdsTcsTaxReport
